import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * Top-of-screen banner that appears when a generation request is rejected
 * because the user has hit their concurrent-runs cap (HTTP 429 from the
 * generation endpoints). Auto-dismisses when the user clicks the X or
 * navigates to a route that triggers a fresh `concurrency:limit-hit`
 * event with empty message.
 *
 * Triggered from anywhere in the app by:
 *   window.dispatchEvent(new CustomEvent("concurrency:limit-hit", {
 *     detail: { message: "Your plan allows 3 concurrent runs..." }
 *   }))
 */
export default function ConcurrencyLimitBanner() {
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onHit = (e) => {
      const msg = e?.detail?.message || "";
      setMessage(msg);
    };
    window.addEventListener("concurrency:limit-hit", onHit);
    return () => window.removeEventListener("concurrency:limit-hit", onHit);
  }, []);

  if (!message) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-50 border-b border-amber-200 shadow-sm"
      data-testid="concurrency-limit-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
        <p className="flex-1 text-sm text-amber-900 leading-snug">
          <span className="font-semibold">Concurrent runs limit reached.</span>{" "}
          {message}
        </p>
        <Link
          to="/billing"
          onClick={() => setMessage("")}
          className="hidden sm:inline-flex items-center text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 transition-colors"
          data-testid="concurrency-banner-upgrade"
        >
          Upgrade plan
        </Link>
        <button
          onClick={() => setMessage("")}
          className="text-amber-700 hover:text-amber-900 transition-colors flex-shrink-0"
          aria-label="Dismiss"
          data-testid="concurrency-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
